//crops-page.tsx

import Button from "@mui/material/Button";
import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Link, useHistory } from "react-router-dom";
import { Crop } from "../models/crop";
import { cropsApi } from "../services/api/crops";
import {
  GridColDef,
  GridRowSelectionModel,
  GridRowClassNameParams,
  GridRowParams,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import { Box } from "@mui/system";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../components/confirm-dialog";
import LoadingBackdrop from "../components/loading-backdrop";
import { Grower } from "../models/grower";
import { UserContext, ErrorContext } from "../components/app-routes";
import Page from "../components/page";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { getCustomLabel } from "../utils/custom-labels";

export const unitSystemLabel = (grower: Grower): string =>
  grower.UnitSystem === "metric" ? "g" : "oz";

export const unitSystemLabelOther = (grower: Grower): string =>
  grower.UnitSystem === "metric" ? "oz" : "g";

const CropsDataGrid = React.memo(
  (props: {
    crops: Crop[] | null | undefined;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    onPaginationModelChange: (model: any) => void;
    page?: number;
  }) => {
    const history = useHistory();
    const context = useContext(UserContext);
    const grower = context.grower;

    if (!grower) {
      throw new Error("Invalid context");
    }

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Crops List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "Archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Crop Name",
        width: 300,
      },
      {
        field: "shortCode",
        headerName: "Code",
        width: 60,
      },
      {
        field: "seedName",
        headerName: "Seed Name",
        width: 150,
      },
      {
        field: "seedVariety",
        headerName: "Seed Variety",
        width: 150,
      },
      {
        field: "seedingRate",
        headerName: `Seeding Rate (${unitSystemLabel(grower)})`,
        width: 150,
      },
      {
        field: "daysToMaturity",
        headerName: "Days To Maturity",
        width: 150,
      },
      {
        field: "cutYield",
        headerName: `Cut Yield (${unitSystemLabel(grower)})`,
        width: 150,
      },
      {
        field: "blackoutDays",
        headerName: "Blackout Days",
        width: 150,
      },
      {
        field: "overnightSoak",
        headerName: "Overnight Soak",
        width: 150,
      },
      {
        field: "soakTimeMinutes",
        headerName: "Soak Time (Minutes)",
        width: 150,
      },
      {
        field: "germinationDays",
        headerName: "Germination Days",
        width: 150,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 150,
      },
    ];

    if (!props.crops) return null;

    return (
      <StripedDataGrid
        storageKey="crops-grid"
        columns={columns}
        rows={props.crops.map((c) => ({
          id: c.id,
          name: c.name,
          shortCode: c.shortCode,
          seedName: c.seed?.name,
          seedVariety: c.seed?.variety,
          seedingRate: c.seedingRate,
          overnightSoak: c.overnightSoak,
          soakTimeMinutes: c.soakTimeMinutes,
          germinationDays: c.germinationDays,
          blackoutDays: c.blackoutDays,
          daysToMaturity: c.daysToMaturity,
          cutYield: c.cutYield,
          notes: c.notes,
          archivedAt: c.archivedAt,
        }))}
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) =>
          history.push(`/home/crops/${params.id}`)
        }
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(p: GridRowClassNameParams) => {
          const row = p.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return p.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
      />
    );
  },
);

export default function CropsPage() {
  const [crops, setCrops] = useState<Crop[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [totalTrashed, setTotalTrashed] = useState(0);
  const { handleError } = useContext(ErrorContext);
  const showArchivedCheckedStorageKey = "cropsShowArchivedChecked";
  const showActiveCheckedStorageKey = "cropsShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );
  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    async function getCrops() {
      try {
        const crops = await cropsApi.getAll();
        if (isMounted.current) setCrops(crops);
      } catch (error) {
        if (isMounted.current) handleError(error);
      }
    }

    getCrops();

    return () => {
      isMounted.current = false;
    };
  }, [totalTrashed, handleError]);

  const handleSelectionChange = useCallback((m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;

    if (Array.isArray(m)) {
      ids = m.map((v) => Number(v));
    } else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }

    setSelected(ids);
  }, []);

  const handleClickTrash = () => {
    setDialogOpen(true);
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setDialogOpen(false);
    if (!okTrash) {
      return;
    }

    setBusy(true);

    let trashed = 0;
    for (let i = 0; i < selected.length; i++) {
      try {
        await cropsApi.trash(selected[i]);

        trashed++;

        mixpanel.track("Delete Crop");
      } catch (error) {
        handleError(error);
        break;
      }
    }

    setTotalTrashed(totalTrashed + trashed);
    setBusy(false);
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  let page = getSearchParamNumber(history, "page", 0);

  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page title={getCustomLabel(grower ?? null, "crops")}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/home/crops/add"
          disabled={busy}
        >
          Add
        </Button>
        <IconButton
          aria-label="delete"
          disabled={busy || selected.length === 0}
          onClick={handleClickTrash}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>
      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <CropsDataGrid
          crops={crops?.filter(
            (c) =>
              (showActiveChecked && c.archivedAt === null) ||
              (showArchivedChecked && c.archivedAt !== null),
          )}
          onSelectionChange={handleSelectionChange}
          onPaginationModelChange={(m) => {
            const params = new URLSearchParams(`page=${m.page}`);
            history.replace({
              search: params.toString(),
            });
          }}
          page={page}
        />
      </Box>
      <ConfirmDialog
        title="Trash Crops"
        description={`Are you sure you want to trash ${selected.length} selected crop${selected.length > 1 ? "s" : ""}?
`}
        open={dialogOpen}
        onClose={handleTrashDialogClose}
      ></ConfirmDialog>
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
